/* Nuvō arch transition: see snippets/nuvo-arch.liquid.
   Leaving: the page dims and blurs, the brand arch draws itself in light (bronze outer arch, cream inner
   line, the bronze half-sun last), then flares through its doorway into a full-screen glow and the browser
   navigates. Arriving: the glow the head snippet painted dissolves through a blur into the page. */
(() => {
  const d = document.documentElement;
  const EASE = 'cubic-bezier(.76,0,.24,1)';
  const OUT = 'cubic-bezier(.16,1,.3,1)';
  const GLOW = 'radial-gradient(circle at 50% 50%, #FFF8EE 0%, #FFE2B8 55%, #F6C68C 100%)';

  /* ---------- Arriving ---------- */
  const arrive = () => {
    if (window.__naTaken || !d.classList.contains('na-arriving')) return;
    window.__naTaken = true;
    const veil = document.createElement('div');
    veil.setAttribute('aria-hidden', 'true');
    veil.className = 'na-veil';
    /* display is explicit: the theme hides every empty div */
    veil.style.cssText = `display:block;background:${GLOW}`;
    document.body.appendChild(veil);
    d.classList.remove('na-arriving');
    /* only opacity and transform move, so phones run it on the compositor (the soft focus is a static backdrop blur, desktop only) */
    veil.animate([
      { opacity: 1, transform: 'scale(1)' },
      { opacity: 0, transform: 'scale(1.08)' },
    ], { duration: 700, easing: 'cubic-bezier(.45,0,.2,1)', fill: 'forwards' }).finished.then(() => veil.remove(), () => veil.remove());
  };
  arrive();
  document.addEventListener('na-arrive', arrive);

  if (matchMedia('(prefers-reduced-motion: reduce)').matches || !Element.prototype.animate) return;

  /* ---------- Leaving ---------- */
  const SEL = 'a.header__heading-link, a.header__menu-item-top[href="/"], a.nfo__logo, a.nh__btn';
  /* The arch from the brand logo (viewBox units). The outer arch is revealed by drawing its centreline as a mask.
     The inner line starts and ends mid-base, under the half-sun, so its two ends meet on a straight run with no seam.
     Behind it all sits the golden-hour sun: a halo and a fan of rays rising from the half-sun. */
  const SUN = [688, 1658];
  /* Beams of light reaching nearly to the screen edges: a soft wide beam and a bright narrow core per ray, wide and
     slim ones alternating, each fading out before its tip. They are painted once onto a canvas (soft edges from
     stacked, ever-wider faint wedges rather than a live blur filter) and only that bitmap is moved, which keeps
     phones smooth. BEAM is in arch viewBox units; the canvas holds the upper half-disc around the sun. */
  const BEAM = 4200;
  const CW = 1600, CR = 790, CH = 800;
  const paintRays = () => {
    const c = document.createElement('canvas');
    c.width = CW; c.height = CH;
    const g = c.getContext('2d');
    const cx = CW / 2, cy = CR;
    g.globalCompositeOperation = 'lighter';
    const wedge = (deg, half, len, fill) => {
      const r = (x) => x * Math.PI / 180;
      g.beginPath();
      g.moveTo(cx, cy);
      g.lineTo(cx + len * Math.cos(r(deg - half)), cy + len * Math.sin(r(deg - half)));
      g.lineTo(cx + len * Math.cos(r(deg + half)), cy + len * Math.sin(r(deg + half)));
      g.closePath();
      g.fillStyle = fill;
      g.fill();
    };
    const grad = (stops) => { const x = g.createRadialGradient(cx, cy, 0, cx, cy, CR); stops.forEach(([o, col]) => x.addColorStop(o, col)); return x; };
    const soft = grad([[0, 'rgba(255,240,204,.9)'], [.22, 'rgba(255,208,138,.5)'], [.6, 'rgba(244,181,103,.16)'], [1, 'rgba(244,181,103,0)']]);
    const core = grad([[0, 'rgba(255,251,240,1)'], [.3, 'rgba(255,226,176,.55)'], [.8, 'rgba(255,208,138,0)']]);
    for (let a = -168, i = 0; a <= -12; a += 13, i++) {
      const w = i % 2 ? 2.2 : 3.4;
      g.globalAlpha = .12;
      [2.2, 1.95, 1.7, 1.48, 1.27, 1.08, .9, .74, .6].forEach((k) => wedge(a, w * k, CR, soft));
      g.globalAlpha = 1;
      wedge(a, .7, CR * .85, core);
    }
    return c;
  };
  const ARCH = '<svg class="na-arch" viewBox="40 40 1296 1920" aria-hidden="true">' +
    '<defs>' +
    '<linearGradient id="naG" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#F3D9B1"/><stop offset=".45" stop-color="#C99A68"/><stop offset=".75" stop-color="#E2BC8C"/><stop offset="1" stop-color="#9A6E44"/></linearGradient>' +
    '<mask id="naM" maskUnits="userSpaceOnUse" x="0" y="0" width="1400" height="2000"><path class="na-draw" d="M172 1960V684A516 491 0 0 1 1204 684V1960" pathLength="1" fill="none" stroke="#fff" stroke-width="270"/></mask></defs>' +
    '<path d="M61 1937V684A627 628 0 0 1 1315 684V1937L1094 1716V684A405.5 355 0 0 0 283 684V1716Z" fill="url(#naG)" mask="url(#naM)"/>' +
    '<path class="na-line" d="M688 1687H436V684A252.5 201.5 0 0 1 941 684V1687Z" pathLength="1" fill="none" stroke="#FFF4E4" stroke-width="58" stroke-linejoin="round"/>' +
    '<path class="na-sun" d="M465 1658A223.5 223 0 0 1 912 1658Z" fill="url(#naG)"/>' +
    '</svg>';

  const warmed = {};
  const warm = (url) => {
    if (warmed[url]) return;
    warmed[url] = true;
    if (HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules')) {
      const s = document.createElement('script');
      s.type = 'speculationrules';
      s.textContent = JSON.stringify({ prerender: [{ source: 'list', urls: [url] }] });
      document.head.appendChild(s);
    } else {
      const l = document.createElement('link');
      l.rel = 'prefetch';
      l.href = url;
      document.head.appendChild(l);
    }
  };
  document.addEventListener('pointerover', (e) => { const a = e.target.closest && e.target.closest(SEL); if (a) warm(a.href); });

  /* The overlay: dimmed page, the arch, and the glow it flares into. */
  const build = (cls, extra = '') => {
    const o = document.createElement('div');
    o.className = cls;
    o.innerHTML = '<span class="na__dim"></span><span class="na-halo"></span>' + ARCH + '<span class="na__glow"></span>' + extra;
    document.body.appendChild(o);
    const svg = o.querySelector('.na-arch');
    const rays = paintRays();
    rays.className = 'na-rays';
    o.insertBefore(rays, o.querySelector('.na-halo'));
    return { o, svg, glow: o.querySelector('.na__glow'), rays, halo: o.querySelector('.na-halo') };
  };

  /* Put the rays, halo and flare on the half-sun (the arch scales around that point, so it does not move).
     Runs once the overlay's words are in, since they shift the arch. */
  const place = ({ svg, rays, halo, glow }) => {
    const r = svg.getBoundingClientRect();
    const u = parseFloat(getComputedStyle(svg).height) / 1920;
    const sx = r.left + r.width / 2, sy = r.top + r.height * .84;
    const rw = 2 * BEAM * u, rh = rw * CH / CW;
    Object.assign(rays.style, { width: rw + 'px', height: rh + 'px', left: sx - rw / 2 + 'px', top: sy - rh * CR / CH + 'px', transformOrigin: `50% ${100 * CR / CH}%` });
    const hs = 1520 * u;
    Object.assign(halo.style, { width: hs + 'px', height: hs + 'px', left: sx - hs / 2 + 'px', top: sy - 60 * u - hs / 2 + 'px' });
    glow.style.left = sx + 'px';
    glow.style.top = sy + 'px';
  };

  /* 0–900ms: dim and blur the page, draw the arch, then the half-sun rises into a golden-hour sun */
  const draw = (parts) => {
    const { o, svg, rays, halo } = parts;
    place(parts);
    o.querySelector('.na__dim').animate([{ opacity: 0 }, { opacity: 1 }], { duration: 300, easing: 'ease-out', fill: 'forwards' });
    svg.animate([{ opacity: 0, transform: 'scale(.86)' }, { opacity: 1, transform: 'scale(1)' }], { duration: 440, easing: OUT, fill: 'forwards' });
    svg.querySelector('.na-draw').animate([{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }], { duration: 480, easing: EASE, fill: 'both' });
    svg.querySelector('.na-line').animate([{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }], { duration: 450, delay: 60, easing: EASE, fill: 'both' });
    svg.querySelector('.na-sun').animate([{ transform: 'scale(0)' }, { transform: 'scale(1)' }], { duration: 360, delay: 320, easing: 'cubic-bezier(.34,1.56,.64,1)', fill: 'both' });
    halo.animate([{ opacity: 0, transform: 'scale(.3)' }, { opacity: 1, transform: 'scale(1)' }], { duration: 520, delay: 420, easing: OUT, fill: 'both' });
    rays.animate([
      { opacity: 0, transform: 'scale(.25) rotate(-6deg)' },
      { opacity: 1, transform: 'scale(1) rotate(0deg)', offset: .55 },
      { opacity: 1, transform: 'scale(1.06) rotate(3deg)' },
    ], { duration: 900, delay: 440, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'both' });
  };

  /* 400ms: flare out of the sun into the glow */
  const flare = ({ svg, glow, rays, halo }, delay) => {
    svg.animate([{ transform: 'scale(1)', opacity: 1 }, { transform: 'scale(7)', opacity: .6 }], { duration: 400, delay, easing: 'cubic-bezier(.7,0,.84,0)', fill: 'forwards' });
    rays.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 300, delay: delay + 60, easing: 'ease-in', fill: 'forwards' });
    halo.animate([{ transform: 'scale(1)' }, { transform: 'scale(3)' }], { duration: 400, delay, easing: 'cubic-bezier(.7,0,.84,0)', fill: 'forwards' });
    return glow.animate([{ opacity: 0, transform: 'scale(.15)' }, { opacity: 1, transform: 'scale(1.6)' }], { duration: 380, delay: delay + 20, easing: 'cubic-bezier(.5,0,.75,0)', fill: 'forwards' }).finished;
  };

  let busy = false;
  document.addEventListener('click', (e) => {
    const a = e.target.closest && e.target.closest(SEL);
    if (!a || busy || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const url = new URL(a.href, location.href);
    if (url.origin !== location.origin) return;
    e.preventDefault();
    busy = true;
    const here = url.pathname === location.pathname && !url.hash;
    if (!here) warm(a.href);

    const parts = build('na');
    parts.o.setAttribute('aria-hidden', 'true');
    draw(parts);
    flare(parts, 660).then(() => {
      if (!here) {
        try { sessionStorage.setItem('nuvo-arch', String(Date.now())); } catch (err) {}
        location.href = a.href;
        return;
      }
      /* Already on that page: settle back at the top and dissolve the glow in place. */
      window.scrollTo(0, 0);
      parts.svg.remove();
      parts.o.querySelector('.na__dim').remove();
      parts.rays.remove();
      parts.halo.remove();
      parts.o.animate([{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(1.08)' }], { duration: 560, easing: OUT, fill: 'forwards' })
        .finished.then(() => { parts.o.remove(); busy = false; }, () => { parts.o.remove(); busy = false; });
    });
  });

  /* The same leave-in-light, for code elsewhere (assets/nuvo-cart.js plays it on the drawer's Check out button).
     No arrival flare is queued: the destination may be Shopify's checkout, which has no arch to dissolve. */
  window.nuvoArch = {
    go(url) {
      if (busy || document.querySelector('.na')) return false;
      busy = true;
      const parts = build('na');
      parts.o.setAttribute('aria-hidden', 'true');
      draw(parts);
      flare(parts, 660).then(() => { location.href = url; });
      return true;
    },
  };

  /* ---------- Added to cart ----------
     Once the theme's add-to-cart request succeeds (its cart drawer opens underneath), the arch and the sun play
     and the closing words appear: the hook and CTA from the approved "Hooks and CTAs - Nuvo Living" sheet (General,
     Golden Hour rows 20 and 23) and the store's own risk-reversal facts. The CTA flares into checkout;
     "Keep browsing", Escape or a tap outside dissolve the overlay and leave the cart drawer in view. */
  const HOOK = 'The best part of a California evening starts right here.';
  const CTA = 'Bring golden hour home';
  const FACTS = ['Made to order', 'Complimentary insured delivery', 'Cancel free within 48 hours'];
  /* Jost Light for the hooks, fetched only on pages that can show one (a product form, or a trip back from checkout) */
  let jost = false;
  const loadJost = () => {
    if (jost) return;
    jost = true;
    const l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = 'https://fonts.googleapis.com/css2?family=Jost:wght@300&display=swap';
    document.head.appendChild(l);
  };
  if (document.querySelector('product-form, form[action*="/cart/add"]')) loadJost();
  const celebrate = (added) => moment({ cls: 'na--atc', eyebrow: 'Added to your cart', hook: HOOK, cta: CTA, added });

  /* Add-ons in the added-to-cart moment: a short, planned list that completes the piece just added, shown between
     the hook and the CTA. Single-option pieces add in place; anything with options (size, gas type) opens its page.
       fire pit / table / bowl  -> a fire glass, a pebble or lava stone, the remote if the ignition is electronic, else an ornament
       fire & water piece       -> media tagged for fire and water (blue, cobalt, turquoise, cerulean), plus the remote if electronic
       water only               -> torches and a fire bowl, to light it after dark
       torches                  -> fire bowls to pair with them
       outdoor kitchen / grill  -> fridge, spice drawer, bottle opener, charging station (fit any island, unlike sized doors)
       fire accessory           -> ornaments and the remote
     Anything already in the cart is skipped. */
  const FIRE_TYPES = ['Fire Pits', 'Fire Pits & Tables', 'Fire Bowls'];
  const ACCESSORY_TYPES = ['Fire Media', 'Fire Ornaments', 'Fire Feature Controls'];
  const KITCHEN_TYPES = ['Outdoor Kitchens', 'Outdoor Grills', 'Outdoor Kitchen Accessories'];
  const KITCHEN_ADDONS = ['videl-compact-outdoor-refrigerator', 'videl-spice-rack-pantry-drawer', 'bottle-opener', 'charging-station'];
  const WATER_ADDONS = ['roman-fire-torch-the-outdoor-plus', 'urn-fire-torch-the-outdoor-plus', 'maya-fire-bowl-the-outdoor-plus'];
  const TORCH_ADDONS = ['maya-fire-bowl-the-outdoor-plus', 'cazo-fire-bowl-the-outdoor-plus', 'sedona-fire-bowl-hammered-copper-the-outdoor-plus'];
  const isElectronic = (added) => {
    const ignition = (added.options_with_values || []).find((o) => /ignition/i.test(o.name));
    return !!ignition && /electronic|low voltage|plug & play/i.test(ignition.value);
  };
  /* which list, decided from the added line alone so the layout is known before anything loads */
  const addonPlan = (added) => {
    if (!added || !added.product_type) return null;
    const type = added.product_type;
    if (FIRE_TYPES.includes(type)) return { kind: 'fire', title: 'Finish your fire feature' };
    if (type === 'Water Features') {
      const fire = /fire/i.test(added.product_title || '') || (added.options_with_values || []).some((o) => /ignition|gas/i.test(o.name)) || !!(added.properties && added.properties['Gas Type']);
      return fire ? { kind: 'firewater', title: 'Finish your fire & water feature' } : { kind: 'list', handles: WATER_ADDONS, title: 'Light it after dark' };
    }
    if (type === 'Torches') return { kind: 'list', handles: TORCH_ADDONS, title: 'Pairs well with' };
    if (KITCHEN_TYPES.includes(type)) return { kind: 'list', handles: KITCHEN_ADDONS, title: 'Complete your outdoor kitchen' };
    if (ACCESSORY_TYPES.includes(type)) return { kind: 'accessory', title: 'Complete the look' };
    return null;
  };
  /* both sources reshaped to { title, url, img, cents, variantId (single available option only) } */
  const fromJs = (p) => ({
    id: p.id, title: p.title, url: '/products/' + p.handle, img: p.featured_image, cents: p.price_min ?? p.price, varies: p.price_varies,
    variantId: p.variants.length === 1 && p.variants[0].available ? p.variants[0].id : null, available: p.available,
  });
  const fromJson = (p) => {
    const prices = p.variants.map((v) => Math.round(parseFloat(v.price) * 100));
    return {
      id: p.id, title: p.title, url: '/products/' + p.handle, img: p.images[0] && p.images[0].src, cents: Math.min(...prices), varies: new Set(prices).size > 1,
      variantId: p.variants.length === 1 && p.variants[0].available ? p.variants[0].id : null, available: p.variants.some((v) => v.available),
      type: p.product_type, tags: p.tags || [],
    };
  };
  let accessories = null;
  const getAccessories = () => accessories || (accessories = fetch0('/collections/fire-media-accessories/products.json?limit=50', { headers: { Accept: 'application/json' } })
    .then((r) => r.json()).then((d) => (d.products || []).map(fromJson)).catch(() => []));
  const finishFor = async (added, plan) => {
    const inCart = await fetch0('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()).then((c) => c.items.map((i) => i.product_id)).catch(() => []);
    const fresh = (p) => p && p.available && p.img && !inCart.includes(p.id) && p.id !== added.product_id;
    if (plan.kind === 'list') {
      const list = await Promise.all(plan.handles.map((h) => fetch0('/products/' + h + '.js', { headers: { Accept: 'application/json' } }).then((r) => (r.ok ? r.json() : null)).then((p) => p && fromJs(p)).catch(() => null)));
      return list.filter(fresh).slice(0, 3);
    }
    const pool = (await getAccessories()).filter((p) => fresh(p) && p.variantId);
    const has = (p, t) => p.tags.includes(t);
    const remote = (p) => p.type === 'Fire Feature Controls';
    let kinds;
    if (plan.kind === 'fire') {
      kinds = [(p) => has(p, 'Fire Glass'), (p) => has(p, 'Fire Pebbles') || has(p, 'Lava Rock'), isElectronic(added) ? remote : (p) => p.type === 'Fire Ornaments'];
    } else if (plan.kind === 'firewater') {
      const wet = (p) => has(p, 'Fire and Water Feature Accessories');
      kinds = [(p) => wet(p) && has(p, 'Fire Glass'), (p) => wet(p) && has(p, 'Fire Pebbles'), isElectronic(added) ? remote : wet];
    } else {
      kinds = [(p) => p.type === 'Fire Ornaments', remote, (p) => p.type === 'Fire Ornaments'];
    }
    const picks = [];
    kinds.forEach((k) => { const p = pool.find((q) => k(q) && !picks.includes(q)); if (p) picks.push(p); });
    return picks;
  };
  const fillFinish = async (o, added, plan) => {
    const row = o.querySelector('.na-atc__finish');
    const picks = await finishFor(added, plan).catch(() => []);
    if (!picks.length || !row.isConnected) { row.remove(); return; }
    const list = row.querySelector('ul');
    picks.forEach((p) => {
      const li = document.createElement('li');
      const name = p.title.split(' | ')[0].split(' — ')[0];
      li.innerHTML = '<img alt="" width="44" height="44"><span><b></b><small></small></span>' +
        (p.variantId ? '<button type="button">Add</button>' : '<a class="na-atc__view">View</a>');
      const src = p.img.startsWith('//') ? 'https:' + p.img : p.img;
      li.querySelector('img').src = src + (src.includes('?') ? '&' : '?') + 'width=120';
      /* the piece's own name only; size and weight are on its page */
      li.querySelector('b').textContent = name;
      li.querySelector('small').textContent = (p.varies ? 'From ' : '') + money(p.cents, (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD');
      if (!p.variantId) {
        /* options to choose first (size, gas type): its page, not a guess */
        const a = li.querySelector('a');
        a.href = p.url;
        a.setAttribute('aria-label', 'View ' + name);
        list.appendChild(li);
        return;
      }
      const btn = li.querySelector('button');
      btn.setAttribute('aria-label', 'Add ' + name + ' to cart');
      btn.addEventListener('click', () => {
        btn.disabled = true;
        btn.textContent = 'Adding';
        /* nuvoQuiet: this button is its own feedback; the moment is already on screen */
        fetch('/cart/add.js', { method: 'POST', nuvoQuiet: true, headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: p.variantId, quantity: 1 }) })
          .then((r) => {
            if (!r.ok) throw new Error(r.status);
            btn.textContent = 'Added';
            li.classList.add('is-added');
            const sidebar = document.getElementById('cart-sidebar');
            if (sidebar && sidebar.reload) sidebar.reload();
          })
          .catch(() => { btn.disabled = false; btn.textContent = 'Add'; });
      });
      list.appendChild(li);
    });
    /* the real cards take the placeholders' place, so nothing around them moves */
    row.querySelectorAll('.is-skel').forEach((el) => el.remove());
    list.querySelectorAll('li').forEach((el, i) => el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 420, delay: i * 70, easing: 'ease-out', fill: 'both' }));
  };

  /* The closing scene shared by "added to cart" and "back from checkout": arch and sun, then the words.
     `item` (back from checkout only) shows the piece still waiting in the cart. */
  const moment = ({ cls, eyebrow, hook, cta: ctaText, item, added }) => {
    if (busy || document.querySelector('.na')) return;
    busy = true;
    loadJost();
    const back = document.activeElement;
    /* known up front, so the arch is drawn at its smaller size from the start rather than shrinking later */
    const plan = addonPlan(added);
    const finishing = !!plan;
    const parts = build('na na--atc ' + cls + (finishing ? ' na--finish' : ''),
      '<div class="na-atc">' +
        '<p class="na-atc__eyebrow"></p>' +
        '<p class="na-atc__hook" id="na-atc-hook"></p>' +
        (item ? '<div class="na-atc__item"><img alt="" width="56" height="56"><span><b></b><small></small></span><em></em></div>' : '') +
        (finishing ? '<div class="na-atc__finish"><p class="na-atc__finish-title"></p><ul>' + '<li class="is-skel" aria-hidden="true"><i></i><span><b>&nbsp;</b><small>&nbsp;</small></span></li>'.repeat(3) + '</ul></div>' : '') +
        '<a class="na-atc__cta" href="/checkout"><span class="na-atc__cta-label"></span><svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 10h12M11 5l5 5-5 5"/></svg></a>' +
        '<p class="na-atc__facts"></p>' +
        '<button type="button" class="na-atc__keep">Keep browsing</button>' +
      '</div>');
    const { o, svg } = parts;
    o.setAttribute('role', 'dialog');
    o.setAttribute('aria-modal', 'true');
    o.setAttribute('aria-labelledby', 'na-atc-hook');
    o.querySelector('.na-atc__eyebrow').textContent = eyebrow;
    o.querySelector('.na-atc__hook').textContent = hook;
    o.querySelector('.na-atc__cta-label').textContent = ctaText;
    if (finishing) o.querySelector('.na-atc__finish-title').textContent = plan.title;
    /* fire media and accessories ship as they are; "Made to order" is only true of the pieces themselves */
    const facts = added && ACCESSORY_TYPES.includes(added.product_type) ? FACTS.filter((f) => f !== 'Made to order') : FACTS;
    o.querySelector('.na-atc__facts').innerHTML = facts.map((f) => '<span>' + f + '</span>').join('');
    if (item) {
      const el = o.querySelector('.na-atc__item');
      if (item.img) el.querySelector('img').src = item.img; else el.querySelector('img').remove();
      el.querySelector('b').textContent = item.title;
      el.querySelector('small').textContent = item.variant;
      el.querySelector('em').textContent = item.price;
    }
    draw(parts);
    if (finishing) setTimeout(() => fillFinish(o, added, plan), 900);

    /* the sun keeps breathing while the words are up */
    parts.halo.animate([{ opacity: 1 }, { opacity: .7 }], { duration: 1600, delay: 940, iterations: Infinity, direction: 'alternate', easing: 'ease-in-out' });
    parts.rays.animate([{ transform: 'scale(1.06) rotate(3deg)' }, { transform: 'scale(1.06) rotate(-3deg)' }], { duration: 6000, delay: 1340, iterations: Infinity, direction: 'alternate', easing: 'ease-in-out' });

    const rise = [{ opacity: 0, transform: 'translateY(16px)' }, { opacity: 1, transform: 'none' }];
    [['.na-atc__eyebrow', 700], ['.na-atc__hook', 820], ['.na-atc__item', 920], ['.na-atc__finish', 920], ['.na-atc__cta', 1020], ['.na-atc__facts', 1180], ['.na-atc__keep', 1300]].forEach(([sel, delay]) => {
      const el = o.querySelector(sel);
      if (el) el.animate(rise, { duration: 700, delay, easing: OUT, fill: 'both' });
    });
    const cta = o.querySelector('.na-atc__cta');
    setTimeout(() => cta.focus({ preventScroll: true }), 1100);

    /* Nobody waits for the show: a tap before everything is in place jumps straight to the finished scene.
       Until then a tap only skips, even where the still-invisible button sits, so nobody lands in checkout by accident. */
    let settled = false;
    setTimeout(() => { settled = true; }, 1500);
    o.addEventListener('click', (e) => {
      if (settled) return;
      e.preventDefault();
      e.stopPropagation();
      settled = true;
      o.getAnimations({ subtree: true }).forEach((x) => { if (x.effect.getTiming().iterations !== Infinity) x.finish(); });
    }, true);

    const words = o.querySelector('.na-atc');
    /* the sun's rays and glow are placed on the half-sun by measurement; whenever the words below change height
       (add-ons arriving, a phone rotating) the arch moves, so they are placed again */
    const ro = 'ResizeObserver' in window ? new ResizeObserver(() => place(parts)) : null;
    if (ro) ro.observe(words);
    const onResize = () => place(parts);
    addEventListener('resize', onResize);
    let closing = false;
    const close = () => {
      if (closing) return;
      closing = true;
      document.removeEventListener('keydown', onKey);
      o.animate([{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(1.04)' }], { duration: 420, easing: OUT, fill: 'forwards' })
        .finished.then(() => { o.remove(); busy = false; if (back && back.focus) back.focus({ preventScroll: true }); }, () => { o.remove(); busy = false; });
      if (ro) ro.disconnect();
      removeEventListener('resize', onResize);
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    o.querySelector('.na-atc__keep').addEventListener('click', close);
    o.querySelector('.na__dim').addEventListener('click', close);
    cta.addEventListener('click', (e) => {
      if (closing || e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      closing = true;
      document.removeEventListener('keydown', onKey);
      /* no prefetch here: requesting /checkout ahead of time can open empty checkout sessions */
      words.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 220, easing: 'ease', fill: 'forwards' });
      [parts.rays, parts.halo].forEach((el) => el.getAnimations().forEach((x) => { if (x.effect.getTiming().iterations === Infinity) x.cancel(); }));
      flare(parts, 120).then(() => { location.href = cta.href; });
    });
  };

  /* Watch the theme's own add-to-cart request; only a successful one triggers the moment.
     The theme (assets/product-form.js) reloads the whole page when the response comes back without the cart
     sections it asked for, which wipes the moment out mid-draw (seen on desktop Chrome). If they are missing,
     they are fetched here and handed to the theme, which then just opens its cart drawer as usual. */
  const fetch0 = window.fetch;
  window.fetch = function (input, init) {
    const req = fetch0.apply(this, arguments);
    const url = typeof input === 'string' ? input : input && input.url;
    /* nuvoQuiet: an add that already has its own feedback (the cart drawer's suggestions) */
    if (!url || (init && init.nuvoQuiet) || !/\/cart\/add(\.js)?(\?|$)/.test(new URL(url, location.href).pathname)) return req;
    const wanted = init && init.body instanceof FormData ? init.body.get('sections') : null;
    return req.then(async (r) => {
      if (!r.ok) return r;
      let out = r;
      if (wanted) {
        try {
          const data = await r.clone().json();
          if (!data.sections) {
            data.sections = await fetch0(`${location.pathname}?sections=${encodeURIComponent(wanted)}`).then((x) => x.json());
            out = new Response(JSON.stringify(data), { status: r.status, statusText: r.statusText, headers: r.headers });
          }
        } catch (err) {}
      }
      /* the theme's reply is the added line itself (or { items } from /cart/add.js); its product type decides
         whether the moment offers fire media */
      let added = null;
      try { const d = await r.clone().json(); added = d.items ? d.items[d.items.length - 1] : d; } catch (err) {}
      celebrate(added);
      return out;
    });
  };

  /* ---------- Back from checkout ----------
     Shopify's checkout cannot run our code, so the hook plays when a shopper who went to checkout lands back in the
     store (the Nuvō logo or "Return to cart" in checkout, or the back button) with the cart still full; a finished
     order empties the cart, so buyers never see it. Leaving for checkout is noted in this tab, and the hook plays
     once per trip. Copy: hook 44 and CTA 41 from the approved "Hooks and CTAs - Nuvo Living" sheet (General), plus
     the same risk-reversal facts; no incentive is offered. */
  const BACK_KEY = 'nuvo-checkout';
  const BACK_HOOK = 'One decision now, years of use every summer after.';
  const BACK_CTA = 'Reserve yours';
  const markCheckout = () => { try { sessionStorage.setItem(BACK_KEY, String(Date.now())); } catch (err) {} };
  document.addEventListener('click', (e) => {
    const a = e.target.closest && e.target.closest('a[href]');
    if (!a) return;
    const url = new URL(a.href, location.href);
    if (url.origin === location.origin && /^\/checkouts?(\/|$)/.test(url.pathname)) markCheckout();
  }, true);
  /* the cart drawer's and cart page's Check out buttons submit the cart form with name="checkout" */
  document.addEventListener('submit', (e) => {
    if ((e.submitter && e.submitter.name === 'checkout') || /\/checkout/.test(e.target.getAttribute('action') || '')) markCheckout();
  }, true);

  const money = (cents, currency) => {
    try { return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100); } catch (err) { return '$' + (cents / 100).toFixed(2); }
  };
  const comeBack = async () => {
    let t = 0;
    try { t = +sessionStorage.getItem(BACK_KEY) || 0; sessionStorage.removeItem(BACK_KEY); } catch (err) {}
    if (!t || Date.now() - t > 6 * 3600e3) return;
    loadJost();
    const cart = await fetch0('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()).catch(() => null);
    if (!cart || !cart.item_count) return;
    const top = cart.items.reduce((a, b) => (b.final_line_price > a.final_line_price ? b : a));
    const more = cart.item_count - top.quantity;
    moment({
      cls: 'na--back',
      eyebrow: 'Still in your cart',
      hook: BACK_HOOK,
      cta: BACK_CTA,
      item: {
        img: top.image ? top.image + (top.image.includes('?') ? '&' : '?') + 'width=160' : '',
        title: top.product_title,
        variant: [top.variant_title, more > 0 ? `+ ${more} more` : ''].filter(Boolean).join(' · '),
        price: money(more > 0 ? cart.total_price : top.final_line_price, cart.currency),
      },
    });
  };
  /* after the page has settled, so the hook is not fighting the first paint */
  const comeBackSoon = () => setTimeout(comeBack, 500);
  if (document.readyState === 'complete') comeBackSoon(); else addEventListener('load', comeBackSoon, { once: true });

  /* Coming back with the browser's back button restores this page from memory with the overlay still on. */
  addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    document.querySelectorAll('.na').forEach((o) => o.remove());
    busy = false;
    comeBackSoon();
  });
})();
